import { ALL_OPERATIONS, OperationStorage } from '../constants/operations';
import { Operation } from '../hooks/useOperations/types';
import { getOperationStorageItem, removeOperationStorageItem, setOperationStorageItem } from './operationsStoragePersistence';

export function tryParseJsonString(str) {
  try {
    return JSON.parse(str);
  } catch {
    return undefined;
  }
}

// The only valid type for operations-selected is a string of an array with operation id(s)
export const getOperationsInStorage = (): string[] => {
  const selectedOperationsInStorage = tryParseJsonString(getOperationStorageItem(OperationStorage.OperationsSelected));

  const isNotParsableJson = !selectedOperationsInStorage;
  const selectedOperationsIsNotArray = !Array.isArray(selectedOperationsInStorage);
  // if not parsable and not an array just remove it from session storage
  if (isNotParsableJson || selectedOperationsIsNotArray) {
    removeOperationStorageItem(OperationStorage.OperationsSelected);
    return [];
  }

  const selectedOperationsAreObjects = typeof selectedOperationsInStorage[0] === 'object';
  // if the parsable is an object attempt to extract operation id if any
  if (selectedOperationsAreObjects) {
    const operationIds = selectedOperationsInStorage.map(({operationId})=>operationId);
    setOperationStorageItem(OperationStorage.OperationsSelected, JSON.stringify(operationIds));
    return operationIds;
  }

  return selectedOperationsInStorage;
};

// The only valid type for operation-selected is a string of the operation id
export const getOperationInStorage = (): string | undefined => {
  const selectedOperationInStorage = getOperationStorageItem(OperationStorage.OperationSelected);
  const selectedOperationInStorageWrongFormat = tryParseJsonString(selectedOperationInStorage);
  
  const selectedOperationIsAnObject = typeof selectedOperationInStorageWrongFormat === 'object';
  const selectedOperationIsAnArray = Array.isArray(selectedOperationInStorageWrongFormat);
  // If operation is an array, object, or null remove it 
  if (selectedOperationIsAnArray || selectedOperationIsAnObject || !selectedOperationInStorage) {
    removeOperationStorageItem(OperationStorage.OperationSelected);
    return undefined;
  }

  return selectedOperationInStorage;
};
type HandleAllOperations = {
  userHasOneOperation: boolean;
  allOperationsWithOutAllId: Operation[];
  operations: Operation[];
  setOperationsSelected: (selectedOperation: Operation[]) => void;
}

const handleAllOperationsSelection = ({
  userHasOneOperation,
  allOperationsWithOutAllId,
  operations,
  setOperationsSelected}: HandleAllOperations) => {
  if (!userHasOneOperation) {
    const allOperationIds = allOperationsWithOutAllId.map(({ operationId }) => operationId);
    setOperationStorageItem(OperationStorage.OperationsSelected, JSON.stringify(allOperationIds));
    setOperationStorageItem(OperationStorage.OperationSelected, ALL_OPERATIONS);
    setOperationsSelected(allOperationsWithOutAllId);
  } else {
    setOperationStorageItem(OperationStorage.OperationSelected, operations[1].operationId);
    setOperationStorageItem(OperationStorage.OperationsSelected, JSON.stringify([operations[1].operationId]));
    setOperationsSelected([operations[1]]);
  }
};

const handleNormalSelection = (selected: Operation[], setOperationsSelected: (selectedOperations: Operation[]) => void, operations: Operation[]) => {
  const getOperationsId = selected.map(({ operationId }) => operationId);
  setOperationStorageItem(OperationStorage.OperationSelected, getOperationsId.length ? getOperationsId[0] : operations[0].operationId);
  setOperationStorageItem(OperationStorage.OperationsSelected, JSON.stringify(getOperationsId));
  setOperationsSelected(selected);
};


export const updateSelectedOperationsFromSessionStorage = (operations: Operation[], setOperationsSelected) => {
  const operationsSelected = getOperationsInStorage();
  const operationSelected = getOperationInStorage();

  const removeAllOperationsItem = ({ operationId }: { operationId: string }) => operationId !== ALL_OPERATIONS;
  const userHasOneOperation = operations.length === 2;
    
  const updateOperations = operationsSelected.length !== 0 ? operations.filter(
    (operation: Operation) => operationsSelected?.includes(operation.operationId)
  ) : [];

  const updateOperation = operationSelected ? operations.find(
    (operation: Operation) => operationSelected === operation.operationId) : undefined;
    // fix session storage if both operation and operations is empty
  if (updateOperations.length === 0 && !updateOperation) {
    setOperationStorageItem(OperationStorage.OperationSelected, operations[1].operationId);
    setOperationStorageItem(OperationStorage.OperationsSelected, JSON.stringify([operations[1].operationId]));
    setOperationsSelected([operations[1]]);
  }
  // fix session storage if operation is empty
  else if(!operationSelected && updateOperations.length && !updateOperation) {


    const allOperationsWithOutAllId = operations.filter(removeAllOperationsItem);
    const allOperationsAreSelected = updateOperations.length === allOperationsWithOutAllId.length;
    if(allOperationsAreSelected) {
      handleAllOperationsSelection({
        userHasOneOperation,
        allOperationsWithOutAllId,
        operations,
        setOperationsSelected});
    }
    else {
      handleNormalSelection(updateOperations, setOperationsSelected, operations);
    }
  }
  // fix session storage is operations is empty
  else if(updateOperations.length === 0 && updateOperation) {
    const allOperationsSelected = updateOperation.operationId === ALL_OPERATIONS;
    const allOperationsWithOutAllId = operations.filter(removeAllOperationsItem);
    if (allOperationsSelected) {
      handleAllOperationsSelection({
        userHasOneOperation, 
        allOperationsWithOutAllId,
        operations, 
        setOperationsSelected});
    }
    else {
      handleNormalSelection([updateOperation], setOperationsSelected, operations);
    }
  }
  else {
    setOperationsSelected(updateOperations.length ? updateOperations : [operations[1]]);
  }
};